import { useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, AreaChart, Area
} from "recharts";
import { Switch } from "@/components/ui/switch";

export default function PermitTrendChart({ trend, monthly, onLoadMonthly }) {
  const [granularity, setGranularity] = useState("annual"); // 'annual' | 'monthly'

  const handleToggle = async (v) => {
    const newG = v ? "monthly" : "annual";
    if (newG === "monthly" && !monthly && onLoadMonthly) {
      await onLoadMonthly();
    }
    setGranularity(newG);
  };

  const isMonthly = granularity === "monthly" && monthly && monthly.length > 0;
  const data = isMonthly ? monthly : trend;

  if (!data || data.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="trend-chart">
      <div className="mb-4 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-slate-900">Permit volume — 10-year trend</h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            Census BPS 2014–2023 · {isMonthly ? "monthly distribution" : "annual"}
          </span>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer" data-testid="trend-granularity-label">
          <span>Annual</span>
          <Switch checked={granularity === "monthly"} onCheckedChange={handleToggle} data-testid="trend-monthly-toggle" />
          <span>Monthly</span>
        </label>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        {isMonthly ? (
          <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="bldgsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0F172A" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#0F172A" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="label"
              stroke="#64748B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "#CBD5E1" }}
              interval={11}
              tickFormatter={(v) => v.slice(0, 4)}
            />
            <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#CBD5E1" }} />
            <Tooltip
              contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }}
              labelStyle={{ color: "#94A3B8", fontFamily: "IBM Plex Mono, monospace" }}
              formatter={(value, name) => [value.toLocaleString(), name]}
            />
            <Area type="monotone" dataKey="bldgs" name="Buildings (monthly est.)" stroke="#0F172A" strokeWidth={1.5} fill="url(#bldgsGrad)" />
          </AreaChart>
        ) : (
          <LineChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="year" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#CBD5E1" }} />
            <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#CBD5E1" }} />
            <Tooltip
              contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }}
              labelStyle={{ color: "#94A3B8", fontFamily: "IBM Plex Mono, monospace" }}
              formatter={(value, name) => [value.toLocaleString(), name]}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="bldgs" name="Buildings permitted" stroke="#0F172A" strokeWidth={2} dot={{ r: 3, fill: "#0F172A" }} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="units" name="Housing units permitted" stroke="#059669" strokeWidth={2} dot={{ r: 3, fill: "#059669" }} activeDot={{ r: 5 }} />
          </LineChart>
        )}
      </ResponsiveContainer>
      {isMonthly && (
        <div className="mt-3 text-[11px] text-slate-500 italic" data-testid="monthly-disclaimer">
          Note: Annual totals are real (Census BPS). The within-year monthly distribution is estimated using the U.S. national construction-starts seasonality curve applied to each year's county total.
        </div>
      )}
    </div>
  );
}
